import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneIncoming, PhoneOff, PhoneCall, User, Building, X, ExternalLink, Voicemail, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, readApiJson, okOrThrow } from '../lib/api';
import { openSharedEventStream } from '../lib/eventStream';
import { describeCaller, HINT_TONES } from '../lib/callerHint';
import { useToast, usePrompt } from './TechComponents';
import { useAuth } from '../hooks/useAuth';
import { useOrg } from './OrgSelector';

// Appel fictif de la visite « Premiers pas » : un vrai appel ne se déclenche pas à la demande.
const APPEL_DEMO = {
  call_id: 'demo', phone_number: '01 23 45 67 89', caller_name: 'Client de démonstration',
  company_name: 'Société exemple', match_type: 'user', answered: false, demo: true,
};

/**
 * IncomingCallNotification — listens to SSE events and shows a popup
 * when an incoming call is detected. Opens the call page in a named window
 * (always reuses the same window = no duplicate tabs).
 *
 * `demo` : carte fictive pour la visite guidée — aucun flux SSE, aucun appel réseau,
 * boutons inertes, pas de messages vocaux, pas de liens CRM.
 * Liens CRM (fiche, « + CRM ») : seulement avec `crm.view` sur l'org active — le même
 * droit que les routes /crm/* (sinon le lien mène à un refus, ex. Tech N1) ; « + CRM »,
 * qui crée le contact, exige en plus `crm.manage`.
 */
export default function IncomingCallNotification({ demo = false } = {}) {
  const promptText = usePrompt();
  const toast = useToast();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { activeOrg } = useOrg();
  const peutCrm = !demo && hasPermission('crm.view', activeOrg?.code);
  // « + CRM » CRÉE le contact (POST /api/crm/contacts/from-call : crm.manage au filet) :
  // crm.view seul (mcf_commercial, mcf_referent_formateur) mènerait encore à un refus.
  const peutCreerContactCrm = peutCrm && hasPermission('crm.manage', activeOrg?.code);
  const [calls, setCalls] = useState([]); // active incoming calls
  const [voicemails, setVoicemails] = useState([]); // new voicemail notifications
  const eventSourceRef = useRef(null);
  const reconnectRef = useRef(null);

  const connect = useCallback(() => {
    if (eventSourceRef.current) return;

    const es = openSharedEventStream()  /* un seul flux SSE par onglet, cf lib/eventStream.js */;
    eventSourceRef.current = es;

    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);

        if (data.type === 'incoming_call') {
          setCalls(prev => {
            // Deduplicate by call_id
            if (prev.some(c => c.call_id === data.call_id)) return prev;
            return [...prev, { ...data, timestamp: Date.now() }];
          });

          // Play notification sound
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRl4FAABXQVZFZm10IBAAAAABAAEAESsAABErAAABAAgAZGF0YToFAACA');
            audio.volume = 0.3;
            audio.play().catch(() => {});
          } catch {}
        }

        if (data.type === 'call_enriched') {
          // Update existing call with enriched identity data
          setCalls(prev => prev.map(c =>
            c.call_id === data.call_id
              ? { ...c, caller_name: data.caller_name, company_name: data.company_name, match_type: data.match_type, caller_hint: data.caller_hint }
              : c
          ));
        }

        if (data.type === 'call_answered') {
          // Mark call as answered (keep visible but change state)
          setCalls(prev => prev.map(c =>
            c.call_id === data.call_id ? { ...c, answered: true } : c
          ));
        }

        if (data.type === 'call_ended' || data.type === 'call_missed') {
          // Remove call after 3s
          setTimeout(() => {
            setCalls(prev => prev.filter(c => c.call_id !== data.call_id));
          }, 3000);
        }

        if (data.type === 'call_summary_ready') {
          // Show summary notification briefly
          setCalls(prev => prev.map(c =>
            c.call_id === data.call_id ? { ...c, summary: data.summary } : c
          ));
        }

        if (data.type === 'new_voicemail') {
          setVoicemails(prev => {
            if (prev.some(v => v.voicemail_id === data.voicemail_id)) return prev;
            return [...prev, { ...data, timestamp: Date.now() }];
          });
          // Auto-remove after 15s
          setTimeout(() => {
            setVoicemails(prev => prev.filter(v => v.voicemail_id !== data.voicemail_id));
          }, 15000);
          // Play notification sound
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRl4FAABXQVZFZm10IBAAAAABAAEAESsAABErAAABAAgAZGF0YToFAACA');
            audio.volume = 0.2;
            audio.play().catch(() => {});
          } catch {}
        }
      } catch {}
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      // Reconnect after 5s
      reconnectRef.current = setTimeout(connect, 5000);
    };
  }, []);

  useEffect(() => {
    if (demo) return undefined; // démonstration : aucun flux SSE
    connect();
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [connect, demo]);

  // Auto-remove old calls after 60s
  useEffect(() => {
    if (demo) return undefined; // démonstration : la carte fictive ne vieillit pas
    const interval = setInterval(() => {
      setCalls(prev => prev.filter(c => Date.now() - c.timestamp < 60000));
    }, 10000);
    return () => clearInterval(interval);
  }, [demo]);

  const handleOpen = (call) => {
    const params = new URLSearchParams({
      phone_number: call.phone_number,
      call_id: call.call_id || '',
    });
    // Named window: always reuses the same one
    window.open(`/incoming-call?${params}`, 'meteris_incoming_call', 'width=1200,height=800');
  };

  const handleDismiss = (callId) => {
    setCalls(prev => prev.filter(c => c.call_id !== callId));
  };

  // Démonstration : la seule carte fictive, jamais les vrais appels ni les messages vocaux.
  const cartes = demo ? [APPEL_DEMO] : calls;
  const messages = demo ? [] : voicemails;

  if (cartes.length === 0 && messages.length === 0) return null;

  return (
    <div style={containerStyle}>
      {/* Voicemail notifications */}
      {messages.map(vm => (
        <div
          key={vm.voicemail_id}
          style={{
            background: '#fff', border: '2px solid #7c3aed', borderRadius: 12,
            padding: 16, boxShadow: '0 8px 32px rgba(124,58,237,0.25), 0 2px 8px rgba(0,0,0,0.1)',
            animation: 'slideIn 0.3s ease-out', cursor: 'pointer',
          }}
          onClick={() => { window.location.href = '/messagerie'; setVoicemails(prev => prev.filter(v => v.voicemail_id !== vm.voicemail_id)); }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Voicemail size={18} style={{ color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#7c3aed', textTransform: 'uppercase' }}>
                Nouveau message vocal
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a3a5c' }}>
                {vm.caller_name || 'Inconnu'}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setVoicemails(prev => prev.filter(v => v.voicemail_id !== vm.voicemail_id)); }}
              style={closeBtn} title="Fermer"
            >
              <X size={14} />
            </button>
          </div>
          {vm.company_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Building size={14} style={{ color: '#e67e22', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#6b7d8f' }}>{vm.company_name}</span>
            </div>
          )}
          {vm.phone && (
            <div style={{ fontSize: 12, color: '#6b7d8f', fontFamily: "'IBM Plex Mono', monospace", marginBottom: 6 }}>
              {vm.phone}
            </div>
          )}
          {vm.summary_short && (
            <div style={{ fontSize: 11, color: '#374151', background: '#f5f3ff', padding: '6px 8px', borderRadius: 4, marginBottom: 8 }}>
              {vm.summary_short}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={(e) => { e.stopPropagation(); window.location.href = '/messagerie'; }}
              style={{ ...openBtn, background: '#7c3aed', flex: 1 }}
            >
              <ExternalLink size={14} />
              Voir la messagerie
            </button>
          </div>
        </div>
      ))}

      {cartes.map(call => {
        const carte = (
        <div key={call.call_id || call.phone_number} style={call.answered ? answeredStyle : notifStyle} data-testid="incoming-call-notif">
          {call.demo && (
            <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '2px 6px', borderRadius: 4, marginBottom: 6 }}>
              Démonstration — aucun appel réel
            </div>
          )}
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={iconWrap}>
              {call.answered ? (
                <Phone size={18} style={{ color: '#fff' }} />
              ) : (
                <PhoneIncoming size={18} style={{ color: '#fff', animation: 'ring 1s ease-in-out infinite' }} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: call.answered ? '#059669' : '#dc2626', textTransform: 'uppercase' }}>
                {call.answered ? 'Appel en cours' : 'Appel entrant'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1a3a5c', fontFamily: "'IBM Plex Mono', monospace" }}>
                {call.phone_number}
              </div>
            </div>
            <button onClick={() => { if (!call.demo) handleDismiss(call.call_id); }} style={closeBtn} title="Fermer">
              <X size={14} />
            </button>
          </div>

          {/* Caller info */}
          {call.caller_name && call.caller_name !== 'Inconnu' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <User size={14} style={{ color: '#2563a8', flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1a3a5c' }}>{call.caller_name}</span>
            </div>
          )}
          {call.company_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Building size={14} style={{ color: '#e67e22', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#6b7d8f' }}>{call.company_name}</span>
            </div>
          )}
          {!call.answered && (() => {
            const vue = describeCaller(call);
            if (!vue) return null;
            const ton = HINT_TONES[vue.tone];
            return (
              <div style={{
                fontSize: 12, marginBottom: 8, fontStyle: 'italic',
                color: ton.color, background: ton.background,
                ...(vue.tone === 'warning'
                  ? { padding: '4px 8px', borderRadius: 4, fontStyle: 'normal', fontWeight: 600 }
                  : {}),
              }}>
                {vue.tone === 'warning' ? '☎ ' : ''}{vue.text}
              </div>
            );
          })()}
          {peutCrm && call.match_type === 'crm_contact' && call.crm_contact && !call.answered && (
            <div style={{ fontSize: 12, color: '#7c3aed', background: '#f5f3ff', padding: '4px 8px', borderRadius: 4, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <User size={12} />
              <span>CRM : <b>{[call.crm_contact.first_name, call.crm_contact.last_name].filter(Boolean).join(' ') || call.crm_contact.email}</b></span>
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/crm/contacts/${call.crm_contact.id}`); }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#7c3aed', padding: 0, marginLeft: 'auto', display: 'inline-flex' }}
                title="Ouvrir fiche CRM"
              ><ExternalLink size={12} /></button>
            </div>
          )}

          {/* Summary preview */}
          {call.summary && (
            <div style={{ fontSize: 11, color: '#059669', background: '#f0fdf4', padding: '6px 8px', borderRadius: 4, marginBottom: 8 }}>
              {call.summary}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 6 }}>
            {!call.answered && (
              <button
                onClick={async (e) => {
                  if (call.demo) return; // démonstration : n'appelle personne
                  const bouton = e.currentTarget;
                  const libelle = bouton.textContent;
                  bouton.disabled = true;
                  bouton.textContent = 'Connexion...';
                  try {
                    await apiFetch('/api/call/dial', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ callee: call.phone_number }),
                    }).then(okOrThrow);
                  } catch (err) {
                    // Refus du serveur : le bouton redevient cliquable, sinon il restait gelé sur « Connexion... ».
                    bouton.disabled = false;
                    bouton.textContent = libelle;
                    toast.error('Appel impossible : ' + err.message);
                  }
                }}
                style={{ ...openBtn, background: '#059669', flex: 2 }}
                data-testid="answer-call-btn"
              >
                <PhoneCall size={14} />
                Decrocher
              </button>
            )}
            <button onClick={() => { if (!call.demo) handleOpen(call); }} style={{ ...openBtn, flex: 1 }} data-testid="open-call-btn">
              <ExternalLink size={14} />
              Fiche
            </button>
            {/* Appel inconnu → bouton rapide 'Créer contact CRM' */}
            {peutCreerContactCrm && call.match_type === 'none' && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const fullName = await promptText(`Créer contact CRM pour ${call.phone_number}\n\nNom (ou Prénom Nom) :`, { defaultValue: '' });
                  if (!fullName || !fullName.trim()) return;
                  const parts = fullName.trim().split(/\s+/);
                  const first_name = parts.length > 1 ? parts[0] : '';
                  const last_name = parts.length > 1 ? parts.slice(1).join(' ') : parts[0];
                  try {
                    const res = await apiFetch('/api/crm/contacts/from-call', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        phone: call.phone_number,
                        first_name, last_name,
                        note: `Appel entrant le ${new Date().toLocaleString('fr-FR')}`,
                      }),
                    }).then(readApiJson);
                    if (res.ok && res.contact?.id) {
                      navigate(`/crm/contacts/${res.contact.id}`);
                    } else {
                      toast.error('Erreur : ' + (res.error || 'inconnue'));
                    }
                  } catch (err) {
                    toast.error('Erreur : ' + err.message);
                  }
                }}
                style={{ ...openBtn, flex: 1, background: '#7c3aed' }}
                title="Créer un contact CRM à partir de cet appel"
              >
                <UserPlus size={14} />
                + CRM
              </button>
            )}
          </div>

          <style>{`@keyframes ring { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(-15deg); } 75% { transform: rotate(15deg); } }`}</style>
        </div>
        );
        // Repère de la visite « Premiers pas », posé sur la SEULE carte de démonstration :
        // le DOM d'une vraie notification reste inchangé (attribut littéral, lu par le garde-fou des repères).
        return call.demo
          ? <div key={call.call_id} data-tour="appel-demo">{carte}</div>
          : carte;
      })}
    </div>
  );
}

const containerStyle = {
  position: 'fixed',
  top: 16,
  right: 'calc(16px + var(--rail-droit-largeur, 0px))',   // à gauche du rail droit (décision du 28/10/2026)
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxWidth: 380,
};

const notifStyle = {
  background: '#fff',
  border: '2px solid #dc2626',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 8px 32px rgba(220,38,38,0.25), 0 2px 8px rgba(0,0,0,0.1)',
  animation: 'slideIn 0.3s ease-out',
};

const answeredStyle = {
  ...notifStyle,
  border: '2px solid #059669',
  boxShadow: '0 4px 16px rgba(5,150,105,0.15)',
};

const iconWrap = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: '#dc2626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const closeBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#6b7d8f',
  padding: 4,
  borderRadius: 4,
};

const openBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  padding: '10px 16px',
  background: '#2563a8',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};
